import axios, { AxiosResponse } from 'axios';
import {
  User,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  TarotReading,
  TarotReadingRequest,
  TarotReadingResult,
  PaymentRequest,
  PaymentResponse,
  ApiError,
  UserMoodResponse,
  UserProfile
} from '../types';

// Configuração base da API
export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

export const getApiOrigin = (): string => {
  try {
    const u = new URL(API_BASE_URL);
    return u.origin;
  } catch {
    return '';
  }
};

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Interceptor para adicionar token de autenticação
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers = config.headers || {};
      (config.headers as any).Authorization = `Bearer ${token}`;
    }

    // Ajusta Content-Type dinamicamente: não force JSON quando enviar FormData
    const isFormData = typeof FormData !== 'undefined' && config.data instanceof FormData;
    if (!isFormData) {
      // Para objetos JSON, deixe o Axios inferir; se não houver, define application/json
      const hasContentType = !!(config.headers && (config.headers as any)['Content-Type']);
      if (!hasContentType && (config.method === 'post' || config.method === 'put' || config.method === 'patch')) {
        (config.headers as any)['Content-Type'] = 'application/json';
      }
    } else {
      // Remover qualquer Content-Type pré-existente para permitir boundary automático
      if (config.headers && (config.headers as any)['Content-Type']) {
        delete (config.headers as any)['Content-Type'];
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para tratar respostas e erros
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      // Só redireciona se havia um token salvo (token expirado)
      // Se não havia token, é login com credenciais erradas — deixa o erro propagar
      if (localStorage.getItem('token')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    if (error.response?.status === 403) {
      const detail = error.response?.data?.detail || '';
      if (detail && detail.indexOf('Email não verificado') !== -1) {
        window.location.href = '/verify';
      }
    }
    return Promise.reject(error);
  }
);

// Serviços de autenticação
export const authService = {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post('/auth/login', credentials);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post('/auth/register', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async registerWithProfile(userData: any): Promise<AuthResponse> {
    try {
      const response: AxiosResponse<AuthResponse> = await api.post('/users/register', userData);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async checkEmail(email: string): Promise<{ success: boolean; message: string }> {
    try {
      const response = await api.get(`/auth/check-email`, { params: { email } });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getCurrentUser(): Promise<User> {
    try {
      const response: AxiosResponse<User> = await api.get('/auth/me');
      return response.data;
    } catch (error) {
      throw error;
    }
  },
  async sendVerification(): Promise<any> {
    const response = await api.post('/auth/send-verification');
    return response.data;
  },
  async verifyCode(code: string): Promise<any> {
    const response = await api.post('/auth/verify', { code });
    return response.data;
  },
};

// Serviços de tarot
export const tarotService = {
  async createReading(reading: TarotReading): Promise<TarotReadingResult> {
    try {
      const response: AxiosResponse<TarotReadingResult> = await api.post('/tarot/reading', reading);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao criar leitura de tarot');
    }
  },

  async getReadingHistory(): Promise<TarotReadingResult[]> {
    try {
      const response: AxiosResponse<TarotReadingResult[]> = await api.get('/tarot/readings');
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao buscar histórico');
    }
  },

  async getAvailableThemes(): Promise<any> {
    try {
      const response = await api.get('/tarot/themes');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async getAvailableSpreads(): Promise<any> {
    try {
      const response = await api.get('/tarot/spreads');
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  async performReading(reading: TarotReadingRequest): Promise<TarotReadingResult> {
    try {
      const response: AxiosResponse<TarotReadingResult> = await api.post('/tarot/reading', reading);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao realizar leitura de tarot');
    }
  },
};

// Serviços de humor do usuário
export const moodService = {
  async getTodayMood(): Promise<UserMoodResponse> {
    const response: AxiosResponse<UserMoodResponse> = await api.get('/mood/today');
    return response.data;
  },
  async setTodayMood(mood: string, targetDate?: string): Promise<UserMoodResponse> {
    const payload: any = { mood };
    if (targetDate) payload.date = targetDate;
    const response: AxiosResponse<UserMoodResponse> = await api.post('/mood', payload);
    return response.data;
  },
};

// Serviços de usuário (perfil completo)
export const userService = {
  async getMe(): Promise<User & { profile?: UserProfile }> {
    const response: AxiosResponse<User & { profile?: UserProfile }> = await api.get('/users/me');
    return response.data;
  },
  async getFullAstralMap(userId?: number): Promise<any> {
    const response = await api.post('/mapa-completo', userId ? { user_id: userId } : {});
    return response.data;
  },
  async generateBirthChart(): Promise<any> {
    const response = await api.post('/users/me/birth-chart/generate');
    return response.data;
  },
  async getBirthChart(): Promise<any> {
    const response = await api.get('/users/me/birth-chart');
    return response.data;
  },
  async updateUser(data: { name?: string; email?: string }): Promise<any> {
    const response = await api.put('/users/me', data);
    return response.data;
  },
  async updateProfile(data: Record<string, any>): Promise<any> {
    const response = await api.put('/users/me/profile', data);
    return response.data;
  },
  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await api.put('/users/me/password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  },
  async deleteAccount(): Promise<void> {
    await api.delete('/users/me');
  },
  async getPaymentHistory(): Promise<{
    payments: Array<{
      id: number;
      amount: number;
      currency: string;
      payment_method: string;
      payment_status: string;
      paid_at: string | null;
      created_at: string;
    }>;
    token_transactions: Array<{
      id: number;
      type: string;
      amount: number;
      source: string;
      source_label: string;
      created_at: string;
    }>;
  }> {
    const r = await api.get('/users/me/payment-history');
    return r.data;
  },
  async getStats(): Promise<{
    total_readings: number;
    consecutive_days: number;
    referrals_count: number;
    achievements_unlocked: string[];
    new_achievements: any[];
    referral_code: string;
    achievements_def: any[];
  }> {
    const response = await api.get('/users/me/stats');
    return response.data;
  },
  async applyReferralCode(code: string): Promise<{ message: string }> {
    const response = await api.post('/users/me/referral/apply', { code });
    return response.data;
  },
};

// Serviços de conexões (sinastria)
export const contactService = {
  async list(): Promise<any[]> {
    const response = await api.get('/contacts');
    return response.data;
  },
  async create(data: {
    name: string;
    relationship_type?: string;
    birth_date: string;
    birth_time?: string;
    birth_city?: string;
    birth_state?: string;
    birth_country?: string;
    notes?: string;
  }): Promise<any> {
    const response = await api.post('/contacts', data);
    return response.data;
  },
  async update(id: number, data: Partial<{
    name: string;
    relationship_type: string;
    birth_date: string;
    birth_time: string;
    birth_city: string;
    birth_state: string;
    birth_country: string;
    notes: string;
  }>): Promise<any> {
    const response = await api.put(`/contacts/${id}`, data);
    return response.data;
  },
  async remove(id: number): Promise<void> {
    await api.delete(`/contacts/${id}`);
  },
  async sinastria(id: number): Promise<{ analysis: string; sinastria_id: number; contact_name: string; relationship_type: string; tokens_balance: number }> {
    const response = await api.post(`/contacts/${id}/sinastria`);
    return response.data;
  },
  async conselho(id: number): Promise<{ advice: string; contact_name: string; tokens_balance: number }> {
    const response = await api.post(`/contacts/${id}/conselho`);
    return response.data;
  },
  async pergunta(id: number, question: string): Promise<{ answer: string; question: string; contact_name: string; tokens_balance: number }> {
    const response = await api.post(`/contacts/${id}/pergunta`, { question });
    return response.data;
  },
  async sinastriaHistory(skip = 0, limit = 30): Promise<SinastriaHistoryEntry[]> {
    const response = await api.get(`/contacts/sinastria-history?skip=${skip}&limit=${limit}`);
    return response.data;
  },
  // Busca a análise mais recente já gerada para esta conexão (com conselho e perguntas
  // anexados), pra reabrir a sinastria completa sem gastar token de novo.
  async getLatestSinastria(id: number): Promise<{ id: number; analysis: string; advice: string | null; follow_up_qa: FollowUpQA[] | null; created_at: string } | null> {
    const response = await api.get(`/contacts/${id}/sinastria-latest`);
    return response.data;
  },
  async composto(id: number): Promise<{ analysis: string; chart_data: any; composto_id: number; contact_name: string; relationship_type: string; tokens_balance: number }> {
    const response = await api.post(`/contacts/${id}/composto`);
    return response.data;
  },
  async davison(id: number): Promise<{ analysis: string; chart_data: any; davison_id: number; contact_name: string; relationship_type: string; tokens_balance: number }> {
    const response = await api.post(`/contacts/${id}/davison`);
    return response.data;
  },
  async getLatestComposto(id: number): Promise<{ id: number; analysis: string; chart_data: any; created_at: string } | null> {
    const response = await api.get(`/contacts/${id}/composto-latest`);
    return response.data;
  },
  async getLatestDavison(id: number): Promise<{ id: number; analysis: string; chart_data: any; created_at: string } | null> {
    const response = await api.get(`/contacts/${id}/davison-latest`);
    return response.data;
  },
};

export interface FollowUpQA {
  question: string;
  answer: string;
  created_at?: string;
}

export interface SinastriaHistoryEntry {
  id: number;
  contact_id: number | null;
  contact_name: string;
  relationship_type: string | null;
  entry_type: 'analise' | 'conselho' | 'pergunta' | 'composto' | 'davison';
  question: string | null;
  content: string;
  advice?: string | null;
  follow_up_qa?: FollowUpQA[] | null;
  chart_data?: any;
  created_at: string;
}

// Exporta o cliente para uso em uploads e outras chamadas diretas
export const apiClient = api;

// Serviços de pagamento
export const paymentService = {
  async createPixPayment(payment: PaymentRequest): Promise<PaymentResponse> {
    try {
      const response: AxiosResponse<PaymentResponse> = await api.post('/payment/pix', payment);
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao criar pagamento PIX');
    }
  },
  async createMercadoPagoPreference(amount: number, description: string): Promise<{ payment_id: number; amount: number; init_point: string; preference_id: string }>{
    try {
      const response = await api.post('/payments/mp/create-reading-preference', { amount, description });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.detail || 'Erro ao criar preferência de pagamento');
    }
  }
};

// Função utilitária para tratar erros da API
export const handleApiError = (error: any): string => {
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.message) {
    return error.message;
  }
  return 'Erro desconhecido';
};

// Função para deletar imagem de tema
export async function deleteThemeImage(key: "amor"|"saude"|"dinheiro") {
  const r = await api.delete(`/theme-images/${key}`, { withCredentials: true });
  return r.data; // { ok, images }
}

export default api;
